import Link from "next/link"
import { formatDistanceToNow } from "date-fns"
import { MapPin, Briefcase, Clock, Calendar, DollarSign } from "lucide-react"
import PreferredBadge from "./PreferredBadge"

interface JobCardProps {
  job: {
    id: string
    title: string
    description: string
    location: string
    trade: string
    budget?: string
    timeline: string
    createdAt: string
    postedBy: {
      id: string
      name: string
      userType: string
      location: string
      profileImage?: string
      preferredHomeowner?: boolean
    }
  }
}

export default function JobCard({ job }: JobCardProps) {
  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-4">
      <div className="px-4 py-5 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
          <div>
            <Link href={`/jobs/${job.id}`} className="text-xl font-bold text-blue-600 hover:underline">
              {job.title}
            </Link>
            <div className="mt-1 flex flex-wrap items-center text-sm text-gray-500 gap-3">
              {job.location && (
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  {job.location}
                </div>
              )}
              {job.trade && (
                <div className="flex items-center">
                  <Briefcase className="h-4 w-4 mr-1" />
                  <span className="capitalize">{job.trade}</span>
                </div>
              )}
              {job.timeline && (
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  <span className="capitalize">
                    {job.timeline === "urgent"
                      ? "Urgent"
                      : job.timeline === "week"
                        ? "Within a week"
                        : job.timeline === "month"
                          ? "Within a month"
                          : job.timeline === "future"
                            ? "Future project"
                            : "Flexible"}
                  </span>
                </div>
              )}
              {job.budget && (
                <div className="flex items-center">
                  <DollarSign className="h-4 w-4 mr-1" />
                  {job.budget}
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 sm:mt-0">
            <Link
              href={`/jobs/${job.id}`}
              className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              View Job
            </Link>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-200 px-4 py-4 sm:px-6">
        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{job.description}</p>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-xs text-gray-500">
          <div className="flex items-center mb-2 sm:mb-0">
            <Calendar className="h-4 w-4 mr-1" />
            Posted {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
          </div>
          {job.postedBy && (
            <div className="flex items-center gap-1.5">
              <Link href={`/profile/${job.postedBy.id}`} className="text-blue-600 hover:underline">
                {job.postedBy.name}
              </Link>
              {job.postedBy.preferredHomeowner && <PreferredBadge size="compact" />}
              {job.postedBy.userType && <span className="capitalize">({job.postedBy.userType})</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

